import React from "react";
import { ExamQuestion, ExamAnswerValue } from "@/types/exams";
import { SkipForward, Undo2 } from "lucide-react";

interface ExamQuestionRendererProps {
  question: ExamQuestion;
  index: number;
  answer: ExamAnswerValue;
  onChange: (value: ExamAnswerValue) => void;
}

export const ExamQuestionRenderer: React.FC<ExamQuestionRendererProps> = ({
  question,
  index,
  answer,
  onChange,
}) => {
  const label = `${index + 1}.`;
  const isSkipped = answer === "__SKIPPED__";

  const handleSkip = () => {
    onChange("__SKIPPED__");
  };

  const handleUndoSkip = () => {
    onChange(null);
  };

  // Skipped state UI - shared for all question types
  if (isSkipped) {
    return (
      <div className="h-full w-full overflow-y-auto rounded-lg" role="region" aria-roledescription="carousel">
        <div className="p-2">
          <div className="mb-2 flex items-start justify-between gap-2">
            <div className="text-md flex flex-1 space-x-2 font-normal leading-relaxed">
              <span className="flex-shrink-0 text-muted-foreground">{label}</span>
              <div className="flex-1">
                <p className="text-base leading-7 text-muted-foreground">
                  {question.content.prompt}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center py-4">
            <button
              type="button"
              onClick={handleUndoSkip}
              className="inline-flex items-center justify-center gap-2 rounded-lg h-10 px-4 py-2 text-sm font-medium bg-accent text-accent-foreground hover:bg-accent/80 transition-colors"
            >
              <Undo2 className="h-4 w-4" />
              <span>Undo Skip</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (question.type === "multiple-choice" || question.type === "true-false") {
    const choices =
      question.type === "true-false"
        ? ["True", "False"]
        : question.content.choices || [];

    const current = typeof answer === "string" ? answer : null;

    return (
      <div className="h-full w-full overflow-y-auto rounded-lg" role="region" aria-roledescription="carousel">
        <div className="p-2">
          {/* Question header + Skip */}
          <div className="mb-2 flex items-start justify-between gap-2">
            <div className="text-md flex flex-1 space-x-2 font-normal leading-relaxed">
              <span className="flex-shrink-0">{label}</span>
              <div className="flex-1">
                <p className="text-base leading-7">
                  {question.content.prompt}
                </p>
              </div>
            </div>

            <div className="hidden flex-shrink-0 sm:block">
              <button
                type="button"
                onClick={handleSkip}
                className="inline-flex h-10 items-center justify-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <SkipForward className="h-4 w-4" />
                <span>Skip</span>
              </button>
            </div>
          </div>

          {/* Choices */}
          <div className="space-y-2">
            {choices.map((choice, i) => {
              const letter = String.fromCharCode(65 + i);
              const isSelected = current === letter;

              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onChange(letter)}
                  className={`flex w-full items-start gap-2 rounded-2xl border-[1.5px] p-2.5 text-left transition-all ${
                    isSelected
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-border text-primary/90 hover:bg-accent/50 hover:text-primary"
                  }`}
                >
                  <span className="ml-1 pt-1 text-sm font-semibold text-muted-foreground">
                    {letter}.
                  </span>
                  <p className="flex-1 text-base leading-7">{choice}</p>
                </button>
              );
            })}
          </div>

          {/* Mobile skip */}
          <div className="mt-2 block text-left sm:hidden">
            <button
              type="button"
              onClick={handleSkip}
              className="inline-flex h-10 items-center justify-center space-x-2 px-0 py-2 text-sm font-medium text-muted-foreground underline"
            >
              <span>Skip Question?</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Short Answer / Fill in the blank – free text
  const textValue = typeof answer === "string" ? answer : "";

  return (
    <div className="h-full w-full overflow-y-auto rounded-lg" role="region" aria-roledescription="carousel">
      <div className="p-2">
        {/* Question header + Skip */}
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="text-md flex flex-1 space-x-2 font-normal leading-relaxed">
            <span className="flex-shrink-0">{label}</span>
            <div className="flex-1">
              <p className="text-base leading-7">
                {question.content.prompt}
              </p>
            </div>
          </div>

          <div className="hidden flex-shrink-0 sm:block">
            <button
              type="button"
              onClick={handleSkip}
              className="inline-flex h-10 items-center justify-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <SkipForward className="h-4 w-4" />
              <span>Skip</span>
            </button>
          </div>
        </div>

        {/* Textarea */}
        <div className="relative">
          <textarea
            placeholder="Type your answer here..."
            className="min-h-[60px] !max-h-[150px] w-full resize-none overflow-hidden rounded-2xl border-[1.5px] border-neutral-200 bg-background px-4 py-3 text-base placeholder:text-muted-foreground focus-visible:outline-none dark:border-neutral-700"
            value={textValue}
            onChange={(e) => onChange(e.target.value || null)}
          />
        </div>

        {/* Mobile skip */}
        <div className="mt-2 block text-left sm:hidden">
          <button
            type="button"
            onClick={handleSkip}
            className="inline-flex h-10 items-center justify-center space-x-2 px-0 py-2 text-sm font-medium text-muted-foreground underline"
          >
            <span>Skip Question?</span>
          </button>
        </div>
      </div>
    </div>
  );
};
